import { cachedJSON } from '../utils/cachedJSON.js';
import { useState, useEffect, useRef } from 'react';

const API = import.meta.env.VITE_API_BASE || '/api';

function relFromNow(iso) {
  if (!iso) return '-';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < -3600_000) return 'PAST';
  if (ms < 0) return 'T-0';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `T−${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `T−${hr}h`;
  return `T−${Math.floor(hr / 24)}d`;
}

function statusClass(s) {
  const v = (s || '').toUpperCase();
  if (v === 'GO') return '';            // green (default v2-launch-status)
  if (v === 'HOLD') return ' v2-launch-tbd'; // dim
  if (v === 'TBD' || v === 'TBC') return ' v2-launch-tbd';
  return '';
}

function statusColor(s) {
  const v = (s || '').toUpperCase();
  if (v === 'GO') return 'var(--green)';
  if (v === 'HOLD') return 'var(--warn)';
  return 'var(--txt-dim)';
}

export default function LaunchTimeline({ onGoto }) {
  const [launches, setLaunches] = useState([]);
  const [, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    function load() {
      cachedJSON(`${API}/launches`)
        .then(d => {
          if (cancelled) return;
          setLaunches(
            (d.launches || [])
              .filter(l => l.net && new Date(l.net).getTime() > Date.now() - 3_600_000)
              .slice(0, 12)
          );
        })
        .catch(() => {});
    }
    load();
    const id = setInterval(load, 300_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Drive countdown updates every 30s (no need for per-second here)
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const upcoming = launches.filter(l => new Date(l.net).getTime() > Date.now() - 3_600_000);

  return (
    <div className="v2-launches" style={{ height: 140, flexShrink: 0, overflow: 'hidden', boxSizing: 'border-box' }}>
      <header className="v2-sec-h v2-launches-h">
        <h3>Upcoming Launches</h3>
        <span className="v2-sec-aux">
          {upcoming.length > 0 ? `${upcoming.length} in preview · Events shows all` : 'no data'}
        </span>
      </header>
      {upcoming.length === 0 ? (
        <p className="v2-lede" style={{ fontSize: 12 }}>Launch data loading…</p>
      ) : (
        <div className="v2-launch-row">
          {upcoming.map(l => (
            <article
              className="v2-launch-card"
              key={l.id} role="button" tabIndex={0}
              onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onGoto?.({launch:l}); } }}
              onClick={() => onGoto?.({ launch: l })}
              style={{ cursor: 'pointer', '--accent': statusColor(l.status) }}
              title={l.mission_desc || l.name}
            >
              <div className="v2-launch-t">{relFromNow(l.net)}</div>
              <div className="v2-launch-veh">{l.vehicle || 'Unknown vehicle'}</div>
              <div className="v2-launch-payload">{l.mission || l.name}</div>
              <div className="v2-launch-pad">{l.pad_location || l.pad_name || '-'}</div>
              <div className="v2-launch-foot">
                <span className={`v2-launch-status${statusClass(l.status)}`}
                      style={{ color: statusColor(l.status) }}>
                  {l.status || 'TBD'}
                </span>
                <span className="v2-launch-prov">{l.agency || ''}</span>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
